import React, { useReducer, useEffect } from 'react';

// GLOBAL DEPENDENCIES
import { useParams } from 'react-router-dom';
import moment from 'moment';
import isString from 'lodash/isString';

// GLOBAL FUNCTIONS
import { apiFetch, formatDollar, modalFunctions } from 'functions';

// GLOBAL COMPONENTS
import Form from 'core/form/Form';
import Button from 'core/tools/Button';
import PriceAdjustmentTable from '../PriceAdjustmentTable';

// GLOBAL VARIABLES
import { ENDPOINTS } from 'endpoints';

// LOCAL VARIABLES
import { IMV_ADJUSTMENT_PRICE_OPTIONS, IMV_ADJUSTMENT_YES_NO_OPTIONS } from '../../../options';

// STYLES
import styles from './styles.module.scss';

const INIT = {
  adjustmentType: 1,
  history: [],
  priceAfterAdjustment: 0,
  adjustmentSchema: 'dollars',
  increase: 1,
  adjustAmount: null,
  valid: false
};

const PriceAdjustment = props => {
  const { imvPrice, adjustmentType, history, priceAfterAdjustment, originalPrice } = props;
  const { recordID: inventoryId } = useParams();

  const reduce = (state, action) => {
    return { ...state, ...action };
  };

  const [state, dispatch] = useReducer(reduce, { ...INIT });

  useEffect(() => {
    dispatch({
      adjustmentType,
      history,
      priceAfterAdjustment,
      adjustmentSchema: [1, 3].includes(adjustmentType) ? 'dollars' : 'percent',
      afterAdjustment: [1, 2].includes(adjustmentType) ? priceAfterAdjustment : imvPrice
    });
  }, [imvPrice, adjustmentType, history, priceAfterAdjustment]);

  const handleSelectAdjustmentType = e => {
    dispatch({
      adjustmentType: e,
      adjustmentSchema: [1, 3].includes(e) ? 'dollars' : 'percent',
      adjustAmount: null,
      afterAdjustment: [1, 2].includes(e) ? state.priceAfterAdjustment : imvPrice
    });
  };

  const handleIncrease = e => {
    dispatch({ increase: parseInt(e.target.value, 10) });
  };

  const handleAdjustAmount = e => {
    const { value } = e.target;
    let adjustedAmount = isString(value) ? parseInt(value.replaceAll(/[^\d.-]/g, ''), 10) : value || 0;
    if (state.adjustmentSchema === 'percent' && adjustedAmount > 20) {
      modalFunctions.error('The adjustment percentage must be a number, not greater than 20%');
      adjustedAmount = null;
    }
    if (adjustedAmount <= 0) adjustedAmount = null;
    dispatch({ adjustAmount: adjustedAmount });
  };

  useEffect(() => {
    let targetPrice = [1, 2].includes(state.adjustmentType) ? state.priceAfterAdjustment : imvPrice;
    let isValid = false;

    if (state.adjustAmount) {
      const adjustedAmount =
        state.adjustmentSchema === 'percent' ? (state.adjustAmount * targetPrice) / 100 : state.adjustAmount;
      targetPrice = state.increase ? targetPrice + adjustedAmount : targetPrice - adjustedAmount;
      isValid = targetPrice !== state.priceAfterAdjustment ? true : false;
    }

    dispatch({ afterAdjustment: targetPrice, valid: isValid });
    
  }, [state.adjustAmount, state.increase, state.priceAfterAdjustment, state.adjustmentSchema, state.adjustmentType, imvPrice]);

  const submitAdjustPrice = () =>
    apiFetch({
      method: 'PUT',
      endpoint: ENDPOINTS.inventory.updateAdjustedPrice,
      params: {
        daily_sales_inventory_id: inventoryId,
        price_after_adjustment: state.afterAdjustment
      },
      onSuccess: () => {
        dispatch({
          priceAfterAdjustment: state.afterAdjustment,
          adjustAmount: null,
          history: [
            ...state.history,
            {
              created_date: moment().format('MM/DD/YYYY'),
              original_price: formatDollar(originalPrice),
              price_after_adjustment: formatDollar(state.afterAdjustment),
              adjustment:
                originalPrice < state.afterAdjustment
                  ? formatDollar(state.afterAdjustment - originalPrice)
                  : `(${formatDollar(originalPrice - state.afterAdjustment)})`
            }
          ]
        });
      },
      loadingMessage: 'Adjusting the price',
      successMessage: 'Price adjusted successfully'
    });

  // RENDER
  return (
    <>
      <Form.Row className="w-100">
        <Form.Col cols={{ sm: 24, lg: 6 }} className={styles.col}>
          <Form.Label>Price Adjustment</Form.Label>
        </Form.Col>
        <Form.Col cols={{ sm: 24, lg: 6 }} className={styles.col}>
          <Form.Select
            placeholder="Select Adjustment Type"
            value={state.adjustmentType}
            onSelect={handleSelectAdjustmentType}
            options={IMV_ADJUSTMENT_PRICE_OPTIONS}
            useFormik={false}
          />
        </Form.Col>
        <Form.Col cols={{ sm: 24, lg: 4 }} className={styles.col}>
          <Form.YesNo
            value={state.increase}
            onChange={handleIncrease}
            options={IMV_ADJUSTMENT_YES_NO_OPTIONS}
            className={styles.checkList}
            useFormik={false}
          />
        </Form.Col>
        <Form.Col cols={{ sm: 24, lg: 8 }} className={styles.col}>
          <Form.Control
            schema={state.adjustmentSchema}
            onChange={handleAdjustAmount}
            value={state.adjustAmount}
            useFormik={false}
            fullWidth={false}
          />
        </Form.Col>
      </Form.Row>
      <Form.Row className="w-100">
        <Form.Col cols={{ sm: 24, lg: 6 }} className={styles.col}>
          <Form.Label>Price After Adjustment</Form.Label>
        </Form.Col>
        <Form.Col cols={{ sm: 24, lg: 18 }} className={styles.col}>
          <Form.Control
            schema="dollars"
            value={state.afterAdjustment}
            className={styles['input--label']}
            disabled
            useFormik={false}
          />
        </Form.Col>
      </Form.Row>
      <div>
        <Button.Submit
          type="button"
          className="m-2 mt-4"
          disabled={!state.valid}
          fullWidth={false}
          onClick={submitAdjustPrice}
        />
      </div>
      <PriceAdjustmentTable adjustmentHistory={state.history} />
    </>
  );
};

// EXPORT
export default PriceAdjustment;
